import React, { useRef, useEffect } from 'react';

export default function NavelSidebar({
  tripConfig,
  sidebarTab, setSidebarTab,
  translateFrom, setTranslateFrom,
  translateTo, translateDirection, translating,
  swapLanguages, speakText, copyToClipboard,
  homeAmount, localAmount,
  handleHomeChange, handleLocalChange,
  exchangeRate, rateLoading,
  openGoogleLens, handlePrint
}) {
  const { translation, currency, weather, packing, accommodation, flights, transport } = tripConfig;
  const tabsRef = useRef(null);
  const indicatorRef = useRef(null);
  const tabNames = ['tools', 'essentials', 'travel'];

  useEffect(() => {
    if (!tabsRef.current || !indicatorRef.current) return;
    const activeIdx = tabNames.indexOf(sidebarTab);
    const tabs = tabsRef.current.querySelectorAll('.navel-sidebar-tab');
    if (tabs[activeIdx]) {
      const tab = tabs[activeIdx];
      indicatorRef.current.style.width = `${tab.offsetWidth}px`;
      indicatorRef.current.style.transform = `translateX(${tab.offsetLeft - 4}px)`;
    }
  }, [sidebarTab]);

  const defaultDir = `${translation.sourceLang}-${translation.targetLang}`;

  const SectionTitle = ({ children }) => (
    <h4 className="navel-h3" style={{ marginBottom: 14 }}>{children}</h4>
  );

  const SectionWrap = ({ children }) => (
    <div style={{ marginBottom: 28 }}>{children}</div>
  );

  return (
    <div className="navel-sidebar">
      {/* Pill Tabs */}
      <div className="navel-sidebar-tabs" ref={tabsRef}>
        <div className="navel-sidebar-tab-indicator" ref={indicatorRef} />
        {tabNames.map(tab => (
          <button key={tab} className={`navel-sidebar-tab ${sidebarTab === tab ? 'active' : ''}`} onClick={() => setSidebarTab(tab)}>
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* ======== TOOLS ======== */}
      {sidebarTab === 'tools' && (
        <>
          <SectionWrap>
            <SectionTitle>Translator</SectionTitle>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <span className="navel-small">
                {translateDirection === defaultDir
                  ? `${translation.sourceLabel} → ${translation.targetLabel}`
                  : `${translation.targetLabel} → ${translation.sourceLabel}`}
              </span>
              <button onClick={swapLanguages} className="navel-btn-icon" style={{ width: 30, height: 30, fontSize: '0.8125rem' }}>⇄</button>
            </div>
            <textarea
              value={translateFrom}
              onChange={(e) => setTranslateFrom(e.target.value)}
              placeholder={translateDirection === defaultDir ? translation.sourcePlaceholder : translation.targetPlaceholder}
              className="navel-input"
              rows="3"
              style={{ resize: 'none', marginBottom: 8 }}
            />
            <textarea
              value={translateTo}
              readOnly
              placeholder={translating ? 'Translating...' : 'Translation appears here...'}
              className="navel-input"
              rows="3"
              style={{ resize: 'none', opacity: 0.7 }}
            />
            {translateTo && (
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <button
                  onClick={() => speakText(translateTo, translateDirection === defaultDir ? translation.targetLang : translation.sourceLang)}
                  className="navel-btn-primary"
                  style={{ padding: '7px 16px', fontSize: '0.6875rem' }}
                >Speak</button>
                <button onClick={() => copyToClipboard(translateTo)} className="navel-btn-secondary" style={{ padding: '7px 16px', fontSize: '0.6875rem' }}>Copy</button>
              </div>
            )}
            {translation.commonPhrases && (
              <p className="navel-small" style={{ marginTop: 12, lineHeight: 1.6 }}>
                <strong>Phrases:</strong> {translation.commonPhrases}
              </p>
            )}
          </SectionWrap>

          <SectionWrap>
            <SectionTitle>Currency</SectionTitle>
            <label className="navel-label" style={{ display: 'block', marginBottom: 6 }}>{currency.home.label}</label>
            <input type="number" value={homeAmount} onChange={(e) => handleHomeChange(e.target.value)} className="navel-input" />
            <p className="navel-small" style={{ textAlign: 'center', margin: '10px 0' }}>
              {rateLoading ? 'Loading...' : `1 ${currency.home.code} = ${exchangeRate.toFixed(2)} ${currency.local.code}`}
            </p>
            <label className="navel-label" style={{ display: 'block', marginBottom: 6 }}>{currency.local.label}</label>
            <input type="number" value={localAmount} onChange={(e) => handleLocalChange(e.target.value)} className="navel-input" />
          </SectionWrap>

          <SectionWrap>
            <button onClick={handlePrint} className="navel-btn-primary" style={{ width: '100%' }}>Export to PDF</button>
          </SectionWrap>
        </>
      )}

      {/* ======== ESSENTIALS ======== */}
      {sidebarTab === 'essentials' && (
        <>
          <SectionWrap>
            <SectionTitle>Weather in {weather.monthLabel}</SectionTitle>
            <p className="navel-body">{weather.summary}</p>
          </SectionWrap>
          <SectionWrap>
            <SectionTitle>Packing</SectionTitle>
            <p className="navel-body">{packing}</p>
          </SectionWrap>
          <SectionWrap>
            <SectionTitle>Accommodation</SectionTitle>
            <p className="navel-body" style={{ marginBottom: 8 }}>
              <a href={`https://www.google.com/search?q=${accommodation.searchQuery}`} target="_blank" rel="noopener noreferrer"
                style={{ fontFamily: 'var(--font-heading)', fontSize: '1rem' }}>
                {accommodation.name}
              </a>
            </p>
            <p className="navel-small" style={{ marginBottom: 8 }}>{accommodation.address}</p>
            {accommodation.room && <p className="navel-body"><strong>Room:</strong> {accommodation.room}</p>}
            <p className="navel-body">Check-in from {accommodation.checkIn}; check-out by {accommodation.checkOut}.</p>
            <p className="navel-body">{accommodation.instructions}</p>
            {accommodation.bookingRef && <p className="navel-small" style={{ marginTop: 6 }}>Ref: {accommodation.bookingRef}</p>}
            {accommodation.price && <p className="navel-small">Cost: {accommodation.price}</p>}
          </SectionWrap>
        </>
      )}

      {/* ======== TRAVEL ======== */}
      {sidebarTab === 'travel' && (
        <>
          <SectionWrap>
            <SectionTitle>Flights</SectionTitle>
            <div style={{ background: 'var(--bg-elevated)', borderRadius: 'var(--radius-md)', padding: 16, marginBottom: 12 }}>
              <p className="navel-label" style={{ marginBottom: 6 }}>Outbound</p>
              <p className="navel-body" style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                {flights.outbound.airline} {flights.outbound.flightNumber}
              </p>
              <p className="navel-small">{flights.outbound.route} &middot; {flights.outbound.date}</p>
              <p className="navel-small">{flights.outbound.times}</p>
            </div>
            <div style={{ background: 'var(--bg-elevated)', borderRadius: 'var(--radius-md)', padding: 16 }}>
              <p className="navel-label" style={{ marginBottom: 6 }}>Return</p>
              <p className="navel-body" style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                {flights.return.airline} {flights.return.flightNumber}
              </p>
              <p className="navel-small">{flights.return.route} &middot; {flights.return.date}</p>
              <p className="navel-small">{flights.return.times}</p>
            </div>
            {flights.bookingRef && <p className="navel-small" style={{ marginTop: 10 }}>Ref: {flights.bookingRef}</p>}
            {flights.baggage && <p className="navel-small">Baggage: {flights.baggage}</p>}
            {flights.passengers && <p className="navel-small">Passengers: {flights.passengers}</p>}
          </SectionWrap>
          <SectionWrap>
            <SectionTitle>Airport Transfer</SectionTitle>
            <p className="navel-body"><strong style={{ color: 'var(--text-primary)' }}>{transport.airportTransfer.name}:</strong> {transport.airportTransfer.details}</p>
            <p className="navel-small" style={{ marginTop: 6 }}>{transport.airportTransfer.note}</p>
          </SectionWrap>
          <SectionWrap>
            <SectionTitle>Getting Around</SectionTitle>
            <p className="navel-body">{transport.gettingAround.modes}</p>
            <p className="navel-small" style={{ marginTop: 6 }}>{transport.gettingAround.appNote}</p>
          </SectionWrap>
        </>
      )}
    </div>
  );
}
